﻿import { useState, useEffect } from 'react';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../config/api';
import {
  ContactModalProps,
  ContactFormData,
  ContactFormErrors,
  ContactMethod
} from '../types/contact';

const ContactModal = ({ isOpen, onClose, onSave, editContact = null, contactTypes = [] }: ContactModalProps) => {
    const { token } = useAuth();
    const [formData, setFormData] = useState<ContactFormData>({
        contact_type: '',
        salutation: '',
        first_name: '',
        last_name: '',
        company: '',
        specialty: '',
        qualifications: '',
        provider_number: '',
        ahpra_number: '',
        website: '',
        phone: '',
        mobile: '',
        fax: '',
        email: '',
        address_line_1: '',
        address_line_2: '',
        suburb: '',
        state: '',
        postcode: '',
        country: 'Australia',
        preferred_contact_method: '',
        argus: '',
        medical_objects: '',
        health_link: '',
        erx: '',
        is_active: true,
        notes: ''
    });

    const [contactMethods, setContactMethods] = useState<ContactMethod[]>([]);
    const [errors, setErrors] = useState<ContactFormErrors>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);

    const salutationOptions = [
        'Dr', 'Prof', 'Mr', 'Ms', 'Mrs', 'Miss', 'Rev', 'Hon'
    ];

    const stateOptions = [
        'NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'
    ];

    // Load contact methods on component mount
    useEffect(() => {
        loadContactMethods();
    }, []);

    // Populate form when editing
    useEffect(() => {
        if (editContact) {
            setFormData({
                contact_type: editContact.contact_type || '',
                salutation: editContact.salutation || '',
                first_name: editContact.first_name || '',
                last_name: editContact.last_name || '',
                company: editContact.company || '',
                specialty: editContact.specialty || '',
                qualifications: editContact.qualifications || '',
                provider_number: editContact.provider_number || '',
                ahpra_number: editContact.ahpra_number || '',
                website: editContact.website || '',
                phone: editContact.phone || '',
                mobile: editContact.mobile || '',
                fax: editContact.fax || '',
                email: editContact.email || '',
                address_line_1: editContact.address_line_1 || '',
                address_line_2: editContact.address_line_2 || '',
                suburb: editContact.suburb || '',
                state: editContact.state || '',
                postcode: editContact.postcode || '',
                country: editContact.country || 'Australia',
                preferred_contact_method: editContact.preferred_contact_method || '',
                argus: editContact.argus || '',
                medical_objects: editContact.medical_objects || '',
                health_link: editContact.health_link || '',
                erx: editContact.erx || '',
                is_active: editContact.is_active ?? true,
                notes: editContact.notes || ''
            });
        } else {
            // Reset form for new contact
            setFormData({
                contact_type: '',
                salutation: '',
                first_name: '',
                last_name: '',
                company: '',
                specialty: '',
                qualifications: '',
                provider_number: '',
                ahpra_number: '',
                website: '',
                phone: '',
                mobile: '',
                fax: '',
                email: '',
                address_line_1: '',
                address_line_2: '',
                suburb: '',
                state: '',
                postcode: '',
                country: 'Australia',
                preferred_contact_method: '',
                argus: '',
                medical_objects: '',
                health_link: '',
                erx: '',
                is_active: true,
                notes: ''
            });
        }
        setErrors({});
    }, [editContact, isOpen]);

    const loadContactMethods = async () => {
        setIsLoadingData(true);
        try {
            const response = await apiCall(ENDPOINTS.CORE.CONTACT_METHODS, {
                method: 'GET'
            }, token || undefined);
            
            if (response.ok) {
                const data = await response.json();
                setContactMethods(data.results || data);
            } else {
                console.error('Failed to load contact methods:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Failed to load contact methods:', error);
        } finally {
            setIsLoadingData(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            const newErrors = { ...errors };
            delete newErrors[name];
            setErrors(newErrors);
        }
    };

    const validateForm = () => {
        const newErrors: ContactFormErrors = {};
        
        if (!formData.contact_type) {
            newErrors.contact_type = 'Contact type is required';
        }
        
        // Either person name or company name is required
        if (!formData.first_name && !formData.last_name && !formData.company) {
            newErrors.name = 'Either person name or company name is required';
        }
        
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        if (formData.website && !formData.website.startsWith('http')) {
            newErrors.website = 'Website must start with http:// or https://';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        
        try {
            await onSave(formData);
        } catch (error) {
            console.error('Error saving contact:', error);
            setErrors({ submit: 'Failed to save contact. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-base-300">
                    <h2 className="text-xl font-semibold">
                        {editContact ? 'Edit Contact' : 'Add New Contact'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost btn-sm btn-square"
                        disabled={isLoading}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Contact Type */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Contact Type *</span>
                            </label>
                            <select
                                name="contact_type"
                                value={formData.contact_type}
                                onChange={handleInputChange}
                                className={`select select-bordered ${errors.contact_type ? 'select-error' : ''}`}
                            >
                                <option value="">Select contact type</option>
                                {contactTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                            {errors.contact_type && (
                                <label className="label">
                                    <span className="label-text-alt text-error">{errors.contact_type}</span>
                                </label>
                            )}
                        </div>

                        {/* Name Information */}
                        <div className="divider">Personal/Company Information</div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Salutation</span>
                                </label>
                                <select
                                    name="salutation"
                                    value={formData.salutation}
                                    onChange={handleInputChange}
                                    className="select select-bordered"
                                >
                                    <option value="">Select</option>
                                    {salutationOptions.map(option => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">First Name</span>
                                </label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Enter first name"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Last Name</span>
                                </label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Enter last name"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Company</span>
                                </label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Company/Organization"
                                />
                            </div>
                        </div>

                        {errors.name && (
                            <div className="alert alert-error">
                                <span>{errors.name}</span>
                            </div>
                        )}

                        {/* Professional Information */}
                        <div className="divider">Professional Information</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Specialty</span>
                                </label>
                                <input
                                    type="text"
                                    name="specialty"
                                    value={formData.specialty}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Medical specialty or field"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Qualifications</span>
                                </label>
                                <input
                                    type="text"
                                    name="qualifications"
                                    value={formData.qualifications}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Professional qualifications"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Provider Number</span>
                                </label>
                                <input
                                    type="text"
                                    name="provider_number"
                                    value={formData.provider_number}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Provider registration number"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">AHPRA Number</span>
                                </label>
                                <input
                                    type="text"
                                    name="ahpra_number"
                                    value={formData.ahpra_number}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="AHPRA registration number"
                                />
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="divider">Contact Information</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Phone</span>
                                </label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Phone number"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Mobile</span>
                                </label>
                                <input
                                    type="text"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Mobile number"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Fax</span>
                                </label>
                                <input
                                    type="text"
                                    name="fax"
                                    value={formData.fax}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Fax number"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Email</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`input input-bordered ${errors.email ? 'input-error' : ''}`}
                                    placeholder="email@example.com"
                                />
                                {errors.email && (
                                    <label className="label">
                                        <span className="label-text-alt text-error">{errors.email}</span>
                                    </label>
                                )}
                            </div>

                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">Website</span>
                                </label>
                                <input
                                    type="url"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleInputChange}
                                    className={`input input-bordered ${errors.website ? 'input-error' : ''}`}
                                    placeholder="https://www.example.com"
                                />
                                {errors.website && (
                                    <label className="label">
                                        <span className="label-text-alt text-error">{errors.website}</span>
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Address */}
                        <div className="divider">Address</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">Address Line 1</span>
                                </label>
                                <input
                                    type="text"
                                    name="address_line_1"
                                    value={formData.address_line_1}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Street address"
                                />
                            </div>

                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">Address Line 2</span>
                                </label>
                                <input
                                    type="text"
                                    name="address_line_2"
                                    value={formData.address_line_2}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Unit, suite, etc."
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Suburb</span>
                                </label>
                                <input
                                    type="text"
                                    name="suburb"
                                    value={formData.suburb}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Suburb"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">State</span>
                                </label>
                                <select
                                    name="state"
                                    value={formData.state}
                                    onChange={handleInputChange}
                                    className="select select-bordered"
                                >
                                    <option value="">Select state</option>
                                    {stateOptions.map(state => (
                                        <option key={state} value={state}>
                                            {state}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Postcode</span>
                                </label>
                                <input
                                    type="text"
                                    name="postcode"
                                    value={formData.postcode}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Postcode"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Country</span>
                                </label>
                                <input
                                    type="text"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Country"
                                />
                            </div>
                        </div>

                        {/* Communication Methods */}
                        <div className="divider">Communication Methods</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Preferred Contact Method</span>
                                </label>
                                <select
                                    name="preferred_contact_method"
                                    value={formData.preferred_contact_method}
                                    onChange={handleInputChange}
                                    className="select select-bordered"
                                    disabled={isLoadingData}
                                >
                                    <option value="">Select method</option>
                                    {contactMethods.map(method => (
                                        <option key={method.id} value={method.id}>
                                            {method.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Argus</span>
                                </label>
                                <input
                                    type="text"
                                    name="argus"
                                    value={formData.argus}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Argus communication details"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Medical Objects</span>
                                </label>
                                <input
                                    type="text"
                                    name="medical_objects"
                                    value={formData.medical_objects}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Medical Objects ID"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Health Link</span>
                                </label>
                                <input
                                    type="text"
                                    name="health_link"
                                    value={formData.health_link}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Health Link ID"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">eRx</span>
                                </label>
                                <input
                                    type="text"
                                    name="erx"
                                    value={formData.erx}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="eRx communication details"
                                />
                            </div>
                        </div>

                        {/* Notes and Status */}
                        <div className="divider">Additional Information</div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Notes</span>
                            </label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                className="textarea textarea-bordered"
                                placeholder="Additional notes or comments"
                                rows={3}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleInputChange}
                                    className="checkbox checkbox-primary mr-2"
                                />
                                <span className="label-text">Active Contact</span>
                            </label>
                        </div>

                        {errors.submit && (
                            <div className="alert alert-error">
                                <span>{errors.submit}</span>
                            </div>
                        )}
                    </form>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-base-300">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-ghost"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="btn btn-primary"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <FaSpinner className="animate-spin mr-2" />
                                Saving...
                            </>
                        ) : (
                            editContact ? 'Update Contact' : 'Create Contact'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContactModal;